import React, { useEffect, useState, useImperativeHandle, useMemo, useCallback } from 'react';
import { Button, Card, Checkbox, Dropdown, Layout, Menu, Table } from 'antd';
import { CaretDownOutlined, PlusOutlined } from '@ant-design/icons'; 
import useLocales from '../../gql/hooks/useLocales'; 
import useBrands from '../../gql/hooks/useBrands'; 
import Spinner from '../Spinner/Spinner';
import Loadable from '../Loadable/Loadable';
import SearchBar from '../SearchBar/SearchBar';
import useSearch from '../../gql/hooks/useSearch'; 
import useHasError from '../../hooks/useHasError'; 
import Breadcrumbs from '../Breadcrumbs/Breadcrumbs';
import useIsLoading from '../../hooks/useIsLoading'; 
import useFilterTags from '../../hooks/useFilterTags'; 
import { useURLQueries } from '../../hooks/useURLQueries'; 
import './styles.less';
import ExpandableAlert from '../../../components/errors/ExpandableAlert';

// Applied to every column except Name: keeps headers and multi-value cells on a
// single line so the greedy Name column can't compress them into wrapping.
const noWrap = {
    onHeaderCell: () => ( { style: { whiteSpace: 'nowrap' } } ),
    onCell: () => ( { style: { whiteSpace: 'nowrap' } } ),
};

const SearchQueryList =  ( { type, tableColumns: _tableColumns, uiStrings,
    breadcrumbs, isDeleting, createClickCallback, renderExpandedRowCallback,
    setExpandedRowsCallback, defaultExpandedRows, extra, ref } ) => {

    const [ expandedRows, setExpandedRows ] = useState( defaultExpandedRows || [] );
    const [ tableData, setTableData ] = useState( [] );
    const { brandsLoading, brandsError, brands, getBrand, getBrandLabel } = useBrands();
    const { localesLoading, localesError, getLocaleLabel } = useLocales();
    const { currentPage, setCurrentPage, searchText, setSearchText, searchWords, 
        sortField, setSortField, sortDirection, setSortDirection, brandsFilter, setBrandsFilter } = useURLQueries();
    const { searchData, searchDataCount, searchError, searchLoading, loadSearch } = useSearch( { type } );
    const { filterTags, setFilterTags } = useFilterTags( ) ;

    const { isLoading } = useIsLoading( { items: [ searchLoading, localesLoading, brandsLoading ] } );
    const { hasError, errorString } = useHasError( { items: [ searchError, localesError, brandsError ] } );

    useEffect( () => setTableData( searchData ), [ searchData ] );
    useEffect( () => loadSearch( { searchText, currentPage, sortField, sortDirection, brandsFilter } ), [] );
  
    useEffect( () => {
        if ( isLoading === false && brands?.length > 0 ) {
            const items =  brandsFilter ? brandsFilter.split( ',' ) : []; 

            setFilterTags( 
                items.map( i => ( 
                    { 
                        id: i, 
                        name: getBrandLabel( i ),
                        onClickCallback: () => handleToggleBrand( i )
                    } 
                ) ) 
            );
        }
    }, [ isLoading, brands ] );

    useImperativeHandle( ref, () => ( {
        handleLocaleChange: ( value, targetItem ) => {
            const result = [ ...tableData ];
    
            const item = result.find( i => i.key === targetItem.key );
            const locale = item.locales.find( i => i.code === value );

            if ( locale ) {
                item.name = locale.name;
                item.content = locale.description;
                item._selectedLocale = value;
                setTableData( result );
            }
        },
        getLabelForLocale: ( i ) => {
            return getLocaleLabel( i );
        },
        getBrand: ( i ) => {
            return getBrand( i );
        },
        reload: () => {
            loadSearch( { searchText, currentPage, sortField, sortDirection, brandsFilter } );  
        },
        getSearchWords: () => {
            return searchWords;
        }
    } ) );
    
    const handleToggleBrand = ( id ) => {
        const filters = brandsFilter ? brandsFilter.split( ',' ) : [];
        let updatedFilters = '';

        if ( filters.includes( id ) ) {
            updatedFilters = filters.filter( i => i !== id ).join();
            setBrandsFilter( updatedFilters );
        } else {
            filters.push( id );
            updatedFilters = filters.join();
            setBrandsFilter( updatedFilters );
        }

        loadSearch( { searchText, currentPage, sortField, sortDirection, brandsFilter: updatedFilters } );  
    };

    const renderBrandMenu = useCallback( ( confirm ) => {
        return (
            <Menu>
                {
                    brands.map( i => (
                        <Menu.Item
                            key={ i.id }  
                        >
                            <Checkbox 
                                data-id={ i.id }
                                checked={ brandsFilter.includes( i.id ) }
                                onChange={ ( e ) => {
                                    handleToggleBrand( i.id );
                                    confirm( {
                                        closeDropdown: true,
                                    } );
                                } }
                            >
                                { i.name }
                            </Checkbox>
                        </Menu.Item>
                    ) )
                }
            </Menu>
        );
    }, [ brands, brandsFilter ] );

    const tableColumns = useMemo( () => _tableColumns.map( c => {
        let col = c;
        switch ( c.sortKey ) {
            case 'name':
                // Greedy column: fills the table's slack space. No nowrap, so a
                // genuinely long name can still wrap inside the roomy column.
                return {
                    ...c,
                    width: '100%',
                    onCell: () => ( { style: { wordBreak: 'break-word' } } ),
                    sortOrder: sortField === 'name' && sortDirection,
                    sorter: searchText ? undefined : ( a, b ) => 0,
                };
            case 'createdAt':
                col = {
                    ...c,
                    sortOrder: sortField === 'createdAt' && sortDirection,
                    sorter: searchText ? undefined : ( a, b ) => 0,
                };
                break;
            case 'updatedAt':
                col = {
                    ...c,
                    sortOrder: sortField === 'updatedAt' && sortDirection,
                    sorter: searchText ? undefined : ( a, b ) => 0,
                };
                break;
            case 'weight':
                col = {
                    ...c,
                    sortOrder: sortField === 'weight' && sortDirection,
                    sorter: searchText ? undefined : ( a, b ) => 0,
                };
                break;
            case 'brands':
                col = {
                    ...c,
                    filterDropdown: ( { confirm } ) => renderBrandMenu( confirm ),
                };
                break;
        }
        // Every non-name column stays on a single line so the greedy Name column
        // can't compress it down to its wrapping width.
        return { ...noWrap, ...col };
    } ), [ _tableColumns, sortField, searchText, sortDirection, renderBrandMenu ] );

    /*************************************
     * ANCHOR Event handlers
     *************************************/
    
    const handleChangeCallback = ( pagination, filters, sorter ) => {
        setCurrentPage( pagination.current );
        setSortField( sorter.column?.dataIndex );
        setSortDirection( sorter.order );  
 
        loadSearch( { 
            searchText, 
            currentPage: pagination.current, 
            sortField: sorter.column?.dataIndex, 
            sortDirection: sorter.order,
            brandsFilter
        } );  
    };

    const handleSearchClick = ( value ) => {
        setCurrentPage( 1 ); 
        setSearchText( value );
        loadSearch( { searchText: value, currentPage: 1, sortField, sortDirection, brandsFilter } );  
    };
    
    const handleSetExpandedRows = ( rows ) => {
        if ( setExpandedRowsCallback ) {
            setExpandedRowsCallback( rows );
        }
        setExpandedRows( rows );
    };

    const handleExpandCollapseClick = ( item ) => {
        if ( item.key === 'expand' ) {
            handleSetExpandedRows( tableData?.map( i => i.id ) || [] ); 
        } else {
            handleSetExpandedRows( [] );
        }
    };

    /*************************************
     *  ANCHOR Render
     *************************************/
   
    return (
        <Layout className="search-query-list-page">
            
            { /* Page header and breadcrumbs */ }
            <Breadcrumbs crumbs={ breadcrumbs }/>

            { /* Loading Spinner */ }
            { isLoading &&
                <Spinner/>
            }
            
            { /* Main Content */ }
            { !isLoading &&
                <Card
                    className="search-query-content"
                    title={ uiStrings.cardTitle }
                    extra={ 
                        <>
                            { extra && extra }

                            { !hasError && createClickCallback &&
                                <Button
                                    icon={ <PlusOutlined /> }
                                    onClick={ () => createClickCallback() }
                                    type="text"
                                >
                                    { uiStrings.createButtonTitle }
                                </Button>
                            }
                        </>
                    }
                >   
                    { /* Error message */ }
                    { hasError && 
                        <ExpandableAlert 
                            message="Could not perform search"
                            error={ errorString }
                        />
                    }
                    
                    { !hasError && 
                        <Loadable loading={ isDeleting }>
                            
                            { /* Search bar */ }
                            <SearchBar
                                searchText={ searchText }
                                filterItems={ filterTags }
                                handleSearchCallback={ handleSearchClick }
                                closeTagCallback={ () => handleSearchClick( '' ) }
                            /> 

                            { /* Main table */ }
                            <Table
                                tableLayout="auto"
                                loading= { searchLoading }
                                columns={ tableColumns }
                                dataSource={ tableData }
                                onChange={ handleChangeCallback }
                                scroll={ { x: 'max-content' } }
                                pagination={ {
                                    current: currentPage,
                                    total: searchDataCount,
                                    position: [ 'bottomRight' ],
                                    showQuickJumper: false,
                                    showSizeChanger: false
                                } }
                                locale={ {
                                    emptyText: <div style={ { minHeight: '400px' } }></div>,
                                } }
                                expandable={ {
                                    expandedRowRender: ( item ) => renderExpandedRowCallback( item ),
                                    expandedRowKeys: expandedRows,
                                    onExpandedRowsChange: handleSetExpandedRows,
                                    columnTitle: (
                                        <Dropdown
                                            menu={ {
                                                items: [
                                                    {
                                                        label: 'Expand All',
                                                        key: 'expand'
                                                    },
                                                    {
                                                        label: 'Collapse All',
                                                        key: 'collapse',
                                                    } 
                                                ],
                                                onClick: handleExpandCollapseClick
                                            } }
                                        >
                                            <Button size="small">
                                                <CaretDownOutlined style={ { color: '#808080' } }/>
                                            </Button>
                                        </Dropdown>
                                    )
                                } }
                            />      
                        </Loadable>
                    }
                </Card>
            }
        </Layout>
    );
};

export default SearchQueryList;
